// FILE: src/utils/firebase/features/system/fetchers.js
//
// Purpose: Unified fetchers for system-scoped data with in-memory + IndexedDB caching.
// - Single TTL constant (TTL_SYSTEM), overridable per-call via opts.ttlMs
// - No duplication via createArrayFetchers() factory
// - Preserves existing export names/keys/behavior
// - Includes system formats (transactions/accounts/tags)

import { getDbOrThrow, mapRawDocs, col } from "../../core/firestoreCore";
import { COLLECTIONS } from "../../core/collections";
import { getDocs, getCountFromServer, collection, query, where, doc, getDoc } from "firebase/firestore";
import { getOrFetch, getOrFetchWithMeta, invalidate as _invalidate } from "../../cache/getOrFetch";

/* ---------- TTL (from .env, overridable per-call) ---------- */
const TTL_SYSTEM =
  Number(process.env.REACT_APP_TTL_SYSTEM_CONFIG_DATA) ||
  7 * 24 * 60 * 60 * 1000; // 7 days

/* ---------- Internal: generic one-shot fetch ---------- */
async function fetchCollectionOnce(collName) {
  const db = getDbOrThrow();
  const snap = await getDocs(col(db, collName));
  return mapRawDocs(snap); // -> array of { id, ...data }
}

/* ---------- Internal: factory to create array fetchers ---------- */
function createArrayFetchers({ key, collName }) {
  const fetch = (opts = {}) => {
    const ttlMs = opts.ttlMs ?? TTL_SYSTEM;
    const fetcher = () => fetchCollectionOnce(collName);

    if (opts.noCache) return fetcher();

    return getOrFetch({
      key,
      ttlMs,
      persist: true,
      persistKey: key,
      fetcher,
    });
  };

  const fetchWithMeta = (opts = {}) => {
    const ttlMs = opts.ttlMs ?? TTL_SYSTEM;
    const fetcher = () => fetchCollectionOnce(collName);

    if (opts.noCache) {
      return fetcher().then((value) => ({ value, fromCache: false }));
    }

    return getOrFetchWithMeta({
      key,
      ttlMs,
      persist: true,
      persistKey: key,
      fetcher,
    });
  };

  const invalidate = () => _invalidate(key);

  return { fetch, fetchWithMeta, invalidate };
}

/* ---------- Helper: pick format doc by version (from an array) ---------- */
/** From an array of docs, pick the one for version (default "v1").
 *  Matches by doc.id === version or doc.version === version; falls back to first doc. */
export function pickSystemFormat(docs, version = "v1") {
  if (!Array.isArray(docs)) return null;
  return (
    docs.find((d) => d?.id === version || d?.version === version) ??
    docs[0] ??
    null
  );
}

/* =========================================================================
 * Existing system fetchers (via factory)
 * ========================================================================= */

const currencyList = createArrayFetchers({
  key: "system:currencyList",
  collName: COLLECTIONS.systemCurrencyList,
});

export const fetchCurrencyList = currencyList.fetch;
export const fetchCurrencyListWithMeta = currencyList.fetchWithMeta;
export const invalidateCurrencyListCache = currencyList.invalidate;

const txnTypes = createArrayFetchers({
  key: "system:transactionTypes",
  collName: COLLECTIONS.systemTransactionTypes,
});
export const fetchTransactionTypes = txnTypes.fetch;
export const fetchTransactionTypesWithMeta = txnTypes.fetchWithMeta;
export const invalidateTransactionTypesCache = txnTypes.invalidate;

const accountTypes = createArrayFetchers({
  key: "system:accountTypes",
  collName: COLLECTIONS.systemAccountTypes,
});
export const fetchAccountTypes = accountTypes.fetch;
export const fetchAccountTypesWithMeta = accountTypes.fetchWithMeta;
export const invalidateAccountTypesCache = accountTypes.invalidate;

/* =========================================================================
 * NEW: System Formats (transactions/accounts/tags) via factory
 * ========================================================================= */

const fmtTxns = createArrayFetchers({
  key: "system:formats:transactions",
  collName: COLLECTIONS.systemFormatsTransactions,
});
export const fetchSystemFormatsTransactions = fmtTxns.fetch;
export const fetchSystemFormatsTransactionsWithMeta = fmtTxns.fetchWithMeta;
export const invalidateSystemFormatsTransactionsCache = fmtTxns.invalidate;

const fmtAccounts = createArrayFetchers({
  key: "system:formats:accounts",
  collName: COLLECTIONS.systemFormatsAccounts,
});
export const fetchSystemFormatsAccounts = fmtAccounts.fetch;
export const fetchSystemFormatsAccountsWithMeta = fmtAccounts.fetchWithMeta;
export const invalidateSystemFormatsAccountsCache = fmtAccounts.invalidate;

const fmtTags = createArrayFetchers({
  key: "system:formats:tags",
  collName: COLLECTIONS.systemFormatsTags,
});
export const fetchSystemFormatsTags = fmtTags.fetch;
export const fetchSystemFormatsTagsWithMeta = fmtTags.fetchWithMeta;
export const invalidateSystemFormatsTagsCache = fmtTags.invalidate;

/* =========================================================================
 * NEW: User data inventory helpers (for NukeDB)
 * ========================================================================= */

/** Return array of entries for COLLECTIONS keys that start with "user". */
export function listUserCollections() {
  const entries = Object.entries(COLLECTIONS)
    .filter(([key]) => key.startsWith("user"))
    .map(([key, name]) => ({ key, name }));
  return entries;
}

/** Count docs for the current user in a given user_* collection. */
export async function countUserCollectionDocs({ user, collectionKey }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const key = String(collectionKey);
  const collName = COLLECTIONS[key];
  if (!collName) throw new Error(`Unknown collection key: ${key}`);

  // Special-case: userTransactionsMeta stores a single doc per user keyed by email/uid
  if (key === "userTransactionsMeta") {
    const byEmail = String(user.email || "").toLowerCase();
    let exists = false;
    if (byEmail) {
      const r1 = await getDoc(doc(collection(db, collName), byEmail));
      exists = r1.exists();
    }
    if (!exists) {
      const r2 = await getDoc(doc(collection(db, collName), String(user.uid)));
      exists = r2.exists();
    }
    return exists ? 1 : 0;
  }

  // Default: count by userId field
  const q = query(collection(db, collName), where("userId", "==", user.uid));
  const snap = await getCountFromServer(q);
  return snap.data().count || 0;
}

/** Convenience: list all user_* collections with counts for the user */
export async function getUserCollectionsWithCounts({ user }) {
  const list = listUserCollections();
  const results = await Promise.all(
    list.map(async (it) => ({
      ...it,
      count: await countUserCollectionDocs({ user, collectionKey: it.key }).catch(() => 0),
    }))
  );
  return results;
}
